import { isNil } from 'lodash';
import { useMemo } from 'react';
import { RRule, Frequency as RRuleFrequency, Options as RRuleOptions } from 'rrule';
import {
  DaysOfWeek,
  getDayOfWeek,
  getNthWeekdayOfMonth,
  getSharedOptions,
  getUTCDate,
} from '../utils';

// Stable empty array to avoid creating new references on every render
const EMPTY_PRESETS: Partial<RRuleOptions>[] = [];

export function useBatchPresets({
  date,
  count,
  until,
  skip,
}: {
  date?: string;
  count?: number;
  until?: Date;
  skip?: boolean;
}): Partial<RRuleOptions>[] {
  // Memoize to prevent creating new arrays on every render
  // Use timestamp for deps to avoid Date reference comparison issues
  const untilTimestamp = until?.getTime();
  return useMemo(() => {
    if (isNil(date) || skip) {
      return EMPTY_PRESETS;
    }
    // Reconstruct Date from timestamp to avoid stale closure issues
    const untilDate = !isNil(untilTimestamp) ? new Date(untilTimestamp) : undefined;
    const sharedOptions = getSharedOptions(date, count, untilDate);
    const dateObj = getUTCDate(new Date(date));
    const weekday = DaysOfWeek[getDayOfWeek(dateObj)];

    const presets: Partial<RRuleOptions>[] = [
      // every day
      {
        ...sharedOptions,
        freq: RRuleFrequency.DAILY,
      },
      // every week on specific day
      {
        ...sharedOptions,
        freq: RRuleFrequency.WEEKLY,
        byweekday: [weekday],
      },
    ];

    const nthWeekdayOfMonth = getNthWeekdayOfMonth(dateObj);
    if (nthWeekdayOfMonth < 5) {
      // every month on nth weekday (e.g., 2nd Monday)
      presets.push({
        ...sharedOptions,
        freq: RRuleFrequency.MONTHLY,
        byweekday: [weekday.nth(nthWeekdayOfMonth)],
      });
    }

    presets.push(
      // monthly on specific date
      {
        ...sharedOptions,
        freq: RRuleFrequency.MONTHLY,
        bymonthday: dateObj.getUTCDate(),
      },
      // yearly on specific month and day
      {
        ...sharedOptions,
        freq: RRuleFrequency.YEARLY,
        bymonth: [dateObj.getUTCMonth() + 1],
        bymonthday: [dateObj.getUTCDate()],
      },
      // every weekday
      {
        ...sharedOptions,
        freq: RRuleFrequency.WEEKLY,
        byweekday: [RRule.MO, RRule.TU, RRule.WE, RRule.TH, RRule.FR],
      },
    );

    return presets;
  }, [date, count, untilTimestamp, skip]);
}
